<template>
  <div v-if="isLoading" class="loading-overlay">
    <div class="loading-container">
      <!-- 主要载入动画 -->
      <div class="loading-main">
        <!-- 代码符号动画 -->
        <div class="code-symbols">
          <div class="symbol" v-for="(symbol, index) in codeSymbols" :key="index" 
               :style="{ animationDelay: index * 0.2 + 's' }">
            {{ symbol }}
          </div>
        </div>
        
        <!-- 中心圆形进度条 -->
        <div class="progress-circle">
          <svg class="progress-ring" width="120" height="120">
            <circle
              class="progress-ring-background"
              stroke="rgba(255, 255, 255, 0.1)"
              stroke-width="4"
              fill="transparent"
              r="52"
              cx="60"
              cy="60"
            />
            <circle
              class="progress-ring-progress"
              stroke="url(#gradient)"
              stroke-width="4"
              fill="transparent"
              r="52"
              cx="60"
              cy="60"
              :stroke-dasharray="circumference"
              :stroke-dashoffset="strokeDashoffset"
            />
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style="stop-color:#43e97b;stop-opacity:1" />
                <stop offset="50%" style="stop-color:#38f9d7;stop-opacity:1" />
                <stop offset="100%" style="stop-color:#667eea;stop-opacity:1" />
              </linearGradient>
            </defs>
          </svg>
          
          <!-- 进度百分比 -->
          <div class="progress-text">
            <span class="progress-number">{{ Math.round(progress) }}%</span>
            <span class="progress-label">载入中</span>
          </div>
        </div>
        
        <!-- 粒子效果 -->
        <div class="particles">
          <div v-for="i in 20" :key="i" class="particle" 
               :style="getParticleStyle(i)"></div>
        </div>
      </div>
      
      <!-- 载入文本动画 -->
      <div class="loading-text">
        <div class="text-line">
          <span v-for="(char, index) in loadingMessage" :key="index" 
                class="char" :style="{ animationDelay: index * 0.1 + 's' }">
            {{ char }}
          </span>
        </div>
        <div class="loading-dots">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
      </div>
      
      <!-- 底部提示 -->
      <div class="loading-tips">
        <div class="tip-item" v-for="(tip, index) in currentTips" :key="index"
             :class="{ active: index === activeTipIndex }">
          {{ tip }}
        </div>
      </div>
    </div>
    
    <!-- 背景动画 -->
    <div class="background-animation">
      <div class="wave wave1"></div>
      <div class="wave wave2"></div>
      <div class="wave wave3"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LoadingAnimation',
  props: {
    duration: {
      type: Number,
      default: 3000 // 3秒载入时间
    }
  },
  data() {
    return {
      isLoading: true,
      progress: 0,
      circumference: 2 * Math.PI * 52,
      loadingMessage: '正在载入个人作品集',
      activeTipIndex: 0,
      codeSymbols: ['{ }', '<>', '( )', '[ ]', '< />', '{ ; }', '< ? >', '< ! >'],
      loadingTips: [
        '正在加载技术栈时间轴...',
        '正在准备旅游足迹展示...',
        '正在初始化音乐播放器...',
        '正在载入运动数据...',
        '正在构建个人仪表盘...',
        '即将完成载入...'
      ]
    }
  },
  computed: {
    strokeDashoffset() {
      return this.circumference - (this.progress / 100) * this.circumference
    },
    currentTips() {
      return this.loadingTips.slice(0, 3) // 只显示3个提示
    }
  },
  mounted() {
    this.startLoading()
  },
  methods: {
    startLoading() {
      const startTime = Date.now()
      const duration = this.duration
      
      // 进度条动画
      const updateProgress = () => {
        const elapsed = Date.now() - startTime
        const newProgress = Math.min((elapsed / duration) * 100, 100)
        this.progress = newProgress
        
        // 更新提示文本
        const tipIndex = Math.floor((newProgress / 100) * this.loadingTips.length)
        this.activeTipIndex = Math.min(tipIndex, this.loadingTips.length - 1)
        
        if (newProgress < 100) {
          requestAnimationFrame(updateProgress)
        } else {
          // 载入完成，延迟一点时间显示完成效果
          setTimeout(() => {
            this.completeLoading()
          }, 500)
        }
      }
      
      requestAnimationFrame(updateProgress)
    },
    
    completeLoading() {
      // 添加完成动画效果
      const container = this.$el.querySelector('.loading-container')
      container.style.transform = 'scale(1.1)'
      container.style.opacity = '0.8'
      
      setTimeout(() => {
        this.isLoading = false
        this.$emit('loading-complete')
      }, 300)
    },
    
    getParticleStyle(index) {
      const angle = (index / 20) * 360
      const radius = 80 + Math.random() * 40
      const x = Math.cos(angle * Math.PI / 180) * radius
      const y = Math.sin(angle * Math.PI / 180) * radius
      
      return {
        left: `calc(50% + ${x}px)`,
        top: `calc(50% + ${y}px)`,
        animationDelay: `${index * 0.1}s`,
        animationDuration: `${2 + Math.random() * 2}s`
      }
    }
  }
}
</script>

<style scoped>
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  overflow: hidden;
}

.loading-container {
  text-align: center;
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
}

.loading-main {
  position: relative;
  margin-bottom: 3rem;
}

/* 代码符号动画 */
.code-symbols {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 300px;
  height: 300px;
}

.symbol {
  position: absolute;
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.5rem;
  font-weight: bold;
  font-family: 'Courier New', monospace;
  animation: symbolFloat 3s infinite ease-in-out;
}

.symbol:nth-child(1) { top: 10%; left: 50%; transform: translateX(-50%); }
.symbol:nth-child(2) { top: 25%; right: 15%; }
.symbol:nth-child(3) { top: 50%; right: 5%; transform: translateY(-50%); }
.symbol:nth-child(4) { bottom: 25%; right: 15%; }
.symbol:nth-child(5) { bottom: 10%; left: 50%; transform: translateX(-50%); }
.symbol:nth-child(6) { bottom: 25%; left: 15%; }
.symbol:nth-child(7) { top: 50%; left: 5%; transform: translateY(-50%); }
.symbol:nth-child(8) { top: 25%; left: 15%; }

@keyframes symbolFloat {
  0%, 100% {
    transform: translateY(0) scale(1);
    opacity: 0.6;
  }
  50% {
    transform: translateY(-20px) scale(1.1);
    opacity: 1;
  }
}

/* 圆形进度条 */
.progress-circle {
  position: relative;
  display: inline-block;
}

.progress-ring {
  transform: rotate(-90deg);
  filter: drop-shadow(0 0 10px rgba(67, 233, 123, 0.5));
}

.progress-ring-progress {
  transition: stroke-dashoffset 0.3s ease;
  stroke-linecap: round;
}

.progress-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
}

.progress-number {
  display: block;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.2rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.progress-label {
  font-size: 0.9rem;
  opacity: 0.8;
  letter-spacing: 1px;
}

/* 粒子效果 */
.particles {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: particleFloat 3s infinite ease-in-out;
}

@keyframes particleFloat {
  0%, 100% {
    transform: scale(0) rotate(0deg);
    opacity: 0;
  }
  50% {
    transform: scale(1) rotate(180deg);
    opacity: 1;
  }
}

/* 载入文本动画 */
.loading-text {
  margin-bottom: 2rem;
}

.text-line {
  margin-bottom: 1rem;
}

.char {
  display: inline-block;
  color: white;
  font-size: 1.2rem;
  font-weight: 500;
  animation: charFadeIn 0.8s ease-in-out forwards;
  opacity: 0;
  transform: translateY(20px);
}

@keyframes charFadeIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.loading-dots {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
}

.dot {
  width: 8px;
  height: 8px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: dotBounce 1.4s infinite ease-in-out both;
}

.dot:nth-child(1) { animation-delay: -0.32s; }
.dot:nth-child(2) { animation-delay: -0.16s; }
.dot:nth-child(3) { animation-delay: 0s; }

@keyframes dotBounce {
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* 底部提示 */
.loading-tips {
  position: absolute;
  bottom: -4rem;
  left: 50%;
  transform: translateX(-50%);
  width: 300px;
}

.tip-item {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.5s ease;
  position: absolute;
  width: 100%;
  text-align: center;
}

.tip-item.active {
  opacity: 1;
  transform: translateY(0);
}

/* 背景动画 */
.background-animation {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 1;
}

.wave {
  position: absolute;
  width: 200%;
  height: 200%;
  background: linear-gradient(45deg, 
    rgba(255, 255, 255, 0.05) 0%, 
    transparent 50%, 
    rgba(255, 255, 255, 0.05) 100%);
  animation: waveMove 8s infinite linear;
}

.wave1 {
  top: -50%;
  left: -50%;
  animation-duration: 8s;
}

.wave2 {
  top: -50%;
  left: -50%;
  animation-duration: 12s;
  animation-direction: reverse;
}

.wave3 {
  top: -50%;
  left: -50%;
  animation-duration: 16s;
}

@keyframes waveMove {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .code-symbols {
    width: 250px;
    height: 250px;
  }
  
  .symbol {
    font-size: 1.2rem;
  }
  
  .progress-number {
    font-size: 1.5rem;
  }
  
  .char {
    font-size: 1rem;
  }
  
  .loading-tips {
    width: 250px;
  }
}

@media (max-width: 576px) {
  .code-symbols {
    width: 200px;
    height: 200px;
  }
  
  .symbol {
    font-size: 1rem;
  }
  
  .progress-ring {
    width: 100px;
    height: 100px;
  }
  
  .progress-number {
    font-size: 1.2rem;
  }
  
  .char {
    font-size: 0.9rem;
  }
  
  .loading-tips {
    width: 200px;
    bottom: -3rem;
  }
  
  .tip-item {
    font-size: 0.8rem;
  }
}

/* 载入完成动画 */
.loading-overlay.fade-out {
  animation: fadeOut 0.5s ease-in-out forwards;
}

@keyframes fadeOut {
  to {
    opacity: 0;
    visibility: hidden;
  }
}
</style>
